import React, { useState, useRef, useEffect } from "react";
import "../../css/BlockPalette.css";
import { useDispatch, useSelector } from "react-redux";
import { addBlockToScript, addCustomSound, setCameraState } from "../../store/sceneSlice";
import ConnectionModal from "../ui/ConnectionModal";
import { sendCommand, isConnected } from "../../utils/deviceConnectionManager";
import PinConfigModal from "../ui/PinConfigModal";
import ColorPickerPopover from "../ui/ColorPickerPopover";
import { startTouchDrag } from "../../utils/touchDragDrop";
import { DEFAULT_MESSAGE_COLOR, getMessageIconPath } from "../../constants/messageBlocks";


// Puzzle backgrounds per category
const puzzleBgByCategory = {
    start: "./assets/blocks/start.svg",
    motion: "./assets/blocks/blueCmd.svg",
    looks: "./assets/blocks/looks.svg",
    sound: "./assets/blocks/sounds.svg",
    control: "./assets/blocks/flow.svg",
    end: "./assets/blocks/endshort.svg",
    humandetection: "./assets/blocks/looks.svg",
    facesensing: "./assets/blocks/looks.svg",
    sensors: "./assets/blocks/flow.svg", // 🛤️ ADDED: Path following category
    otto: "./assets/blocks/blueCmd.svg",
    esp32: "./assets/blocks/blueCmd.svg",
    robot: "./assets/blocks/blueCmd.svg",
    stembot: "./assets/blocks/looks.svg",
};

// Bar color per category for seamless effect
const barColorByCategory = {
    start: "#ffe55a",
    motion: "#68acfc",
    looks: "#eb7dfa",
    sound: "#59de80",
    control: "#ffc862",
    end: "#e84141",
    humandetection: "#eb7dfa",
    facesensing: "#68acfc",
    sensors: "#ffc862", // 🛤️ ADDED: Path following category color
    robot: "#4a90e2",
    stembot: "#b59aff",
};

// All blocks per category
const blocksByCategory = {

    start: [
        { name: "Start on Green Flag", icon: "./assets/blockicons/greenFlag.svg" },
        { name: "Start On Tap", icon: "./assets/blockicons/OnTouch.svg" },
        { name: "Start On Bump", icon: "./assets/blockicons/Bump.svg" },
        {
            name: "Start On Message",
            icon: getMessageIconPath("startOnMessage", DEFAULT_MESSAGE_COLOR),
            type: "startOnMessage",
            messageColor: DEFAULT_MESSAGE_COLOR,
            // puzzleBg: "./assets/blocks/eh/startH.png",
        },
        {
            name: "Send Message",
            icon: getMessageIconPath("sendMessage", DEFAULT_MESSAGE_COLOR),
            type: "sendMessage",
            messageColor: DEFAULT_MESSAGE_COLOR,
            puzzleBg: "./assets/blocks/yellowCmd.svg"
        },
    ],
    motion: [
        { name: "Move Right", icon: "./assets/blockicons/Foward.svg" },
        { name: "Move Left", icon: "./assets/blockicons/Back.svg" },
        { name: "Move Up", icon: "./assets/blockicons/Up.svg" },
        { name: "Move Down", icon: "./assets/blockicons/Down.svg" },
        { name: "Rotate Right", icon: "./assets/blockicons/Right.svg" },
        { name: "Rotate Left", icon: "./assets/blockicons/Left.svg" },
        { name: "Hop", icon: "./assets/blockicons/Hop.svg" },
        { name: "Go To Random Position", icon: "./assets/blockicons/Go_to_Random.svg", type: "go_random_position" },
        { name: "Go Home", icon: "./assets/blockicons/Home.svg" },
    ],
    looks: [
        {
            name: "Say", icon: "./assets/blockicons/Say.svg",
            type: "say",
            parameters: [
                {
                    name: "message",
                    type: "string",
                    defaultValue: "hi"
                }
            ]
        },
        {
            name: "Run Script Text",
            icon: "./assets/blockicons/script.svg",
            type: "scriptText",
            scriptText: ""
        },
        { name: "Grow Size", icon: "./assets/blockicons/Grow.svg", count: 2 },
        { name: "Shrink Size", icon: "./assets/blockicons/Shrink.svg", count: 2 },
        { name: "Reset Size", icon: "./assets/blockicons/Reset.svg" },
        { name: "Appear", icon: "./assets/blockicons/Appear.svg" },
        { name: "Disappear", icon: "./assets/blockicons/Disappear.svg" },
    ],
    sound: [
        { name: "Pop", icon: "./assets/blockicons/Speaker.svg" },
        { name: "Record", icon: "./assets/blockicons/Microphone.svg" },
        { name: "Voice Command", icon: "./assets/blockicons/Microphone.svg", type: "voice_command" },
        {
            name: "Speak",
            icon: "./assets/blockicons/Say.svg",
            type: "ttsSpeak",
            parameters: [
                {
                    name: "message",
                    type: "string",
                    defaultValue: "Hello!"
                }
            ]
        },
        {
            name: "Set Language",
            icon: "./assets/blockicons/language.svg",
            type: "ttsSetLanguage",
            selectedLang: "en-US"
        },
    ],
    control: [
        {
            name: "Wait", icon: "./assets/blockicons/Wait.svg",
            execute: async (actor, dispatch, sounds, actorId, count = 3) => {
                return new Promise(resolve => {
                    setTimeout(resolve, count * 1000);
                });
            }
        },
        {
            name: "Stop", icon: "./assets/blockicons/Stop.svg",
            execute: () => {
                throw new Error("STOP_EXECUTION");
            }
        },
        { name: "Speed", icon: "./assets/blockicons/speed0.svg" },
        {
            name: "Repeat",
            icon: "./assets/blockicons/Repeat.svg",  // You'll need this SVG
            type: "repeat",
            puzzleBg: "./assets/blocks/repeat.png",
            count: 4,  // Default repeat count
            children: [],  // Container for nested blocks
            isContainer: true,  // Flag to identify container blocks
        },
    ],
    device: [
        { name: "Otto-Bot", icon: "./assets/blockicons/OttoBot.svg", requiresConnection: true },
        { name: "Light RC", icon: "./assets/blockicons/RCCar.svg", requiresConnection: true },
    ],
    end: [
        { name: "End", icon: null, label: "" },
        { name: "Repeat Forever", icon: "./assets/blockicons/Forever.svg", type: "repeatBlock" },
        {
            name: "Go To Page",
            icon: "./assets/blockicons/GoToPage.svg",
            type: "goToPage",
            count: 2,
        },
    ],
    humandetection: [
        { name: "Camera Control", icon: "./assets/ui/camera.svg", type: "camera" },
        {
            name: "Go To Face Part",
            icon: "./assets/ui/FaceDetection.svg",
            type: "facePartAction",
            part: "nose", execute: () => true,
        },
        {
            name: "Go To Hand Part",
            icon: "./assets/ui/go_to_finger.svg",
            type: "handPartAction",
            finger: "index finger", execute: () => true,
        },

        {
            name: "Color Detected",
            icon: "./assets/ui/colordetection.svg",
            type: "colorDetected",
            targetColor: "#FF0000",
        },
        {
            name: "Emotion Detected",
            icon: "./assets/blockicons/Smile.svg",
            type: "emotionDetected",
            expression: "happy",
        },
        {
            name: "Move With Hand Detection",
            icon: "./assets/ui/move_with_finger.svg",
            type: "moveWithHandDetection",
            execute: async () => true,
        },
        { name: "Set Video Transparency", icon: "./assets/blockicons/opacity.svg", type: "video_transparency", options: [100, 75, 50, 25, 0] },
        { name: "Sync Actors with Faces", icon: "./assets/blockicons/actor.svg", type: "sync_actors_with_faces" },
    ],
    // 🛤️🎨 UPDATED: Enhanced sensors category with path following AND color detection
    // facesensing: [
    //     { name: "Go To Face Part", icon: "./assets/ui/FaceDetection.svg", type: "facePartAction", part: "nose", execute: () => true, },
    //     { name: "Go To Hand Part", icon: "./assets/ui/go_to_finger.svg", type: "handPartAction", finger: "thumb", execute: () => true, },
    // ],
    sensors: [
        {
            name: "Move Forward",
            icon: "./assets/blockicons/Foward.svg",
            type: "sim_move_forward",
            count: 1,
        },
        {
            name: "Move Backward",
            icon: "./assets/blockicons/Back.svg",
            type: "sim_move_backward",
            count: 1,
        },
        {
            name: "Move Up",
            icon: "./assets/blockicons/Up.svg",
            type: "sim_move_up",
            count: 1,
        },
        {
            name: "Move Down",
            icon: "./assets/blockicons/Down.svg",
            type: "sim_move_down",
            count: 1,
        },
        {
            name: "Turn Left",
            icon: "./assets/blockicons/Left.svg",
            type: "sim_turn_left",
        },
        {
            name: "Turn Right",
            icon: "./assets/blockicons/Right.svg",
            type: "sim_turn_right",
        },
        {
            name: "Set Path Color",
            icon: "./assets/blockicons/Setpathcolor.svg", // Using existing icon
            type: "setPathColor",
            pathColor: "#000000", // Default black path
            execute: () => {
                // Color setting logic handled in runScript.js
                return true;
            }
        },
        // {
        // name: "Start Color Detection",
        // icon: "./assets/ui/image.png", // Using camera icon
        // type: "startColorDetection",
        // targetColor: "#000000", // Default black
        // execute: () => {
        // return true; // Logic handled in runScript.js
        // }
        // },
        // {
        //     name: "Stop Color Detection",
        //     icon: "./assets/blockicons/Stop.svg",
        //     type: "stopColorDetection",
        //     execute: () => {
        //         return true; // Logic handled in runScript.js
        //     }
        // },
        // {
        // name: "Color Detected",
        // icon: "./assets/blockicons/Smile.svg", // Using existing icon
        // type: "colorDetected",
        // execute: () => {
        // return window.colorDetectionActive || false;
        // }
        // },
        {
            name: "Follow Path",
            icon: "./assets/blockicons/followpath.svg",
            type: "followPath",
            requiresColorDetection: true,
            execute: () => {
                return true; // Logic handled in runScript.js
            }
        },
    ],
    otto: [
        { name: "Move Right", icon: "./assets/blockicons/Foward.svg" },
        { name: "Move Left", icon: "./assets/blockicons/Back.svg" },
        { name: "Move Up", icon: "./assets/blockicons/Up.svg" },
        { name: "Move Down", icon: "./assets/blockicons/Down.svg" },
        { name: "Rotate Right", icon: "./assets/blockicons/Right.svg" },
        { name: "Rotate Left", icon: "./assets/blockicons/Left.svg" },
    ],
    esp32: [
        // ⚡ ESP32 Pins — new proper icons
        { name: "Set Digital Pin", icon: "./assets/blockicons/SetDigital.svg", type: "setDigitalPin" },
        // { name: "Read Digital Pin", icon: "./assets/blockicons/ReadDigital.svg", type: "readDigitalPin" },
        { name: "Read Analog Pin", icon: "./assets/blockicons/ReadAnalog.svg", type: "readAnalogPin" },
        { name: "Set PWM Pin", icon: "./assets/blockicons/PWM.svg", type: "setPWM" },
        { name: "Set DAC Pin", icon: "./assets/blockicons/DAC.svg", type: "setDAC" },
    ],

    // 🤖 ROBOT MODE BLOCKS - HP Robots Otto Style
    robot: [
        // {
        //     name: "Move Forward",
        //     icon: "./assets/blockicons/Foward.svg",
        //     type: "robot_move_forward",
        //     count: 20,
        // },
        // {
        //     name: "Move Backward",
        //     icon: "./assets/blockicons/Back.svg",
        //     type: "robot_move_backward",
        //     count: 20,
        // },
        // {
        //     name: "Turn Left",
        //     icon: "./assets/blockicons/Left.svg",
        //     type: "robot_turn_left",
        //     count: 90,
        // },
        // {
        //     name: "Turn Right",
        //     icon: "./assets/blockicons/Right.svg",
        //     type: "robot_turn_right",
        //     count: 90,
        // },
        //   {
        //       name: "Stop",
        //       icon: "./assets/blockicons/Stop.svg",
        //       type: "robot_stop",
        //   },
          // 🦿 SERVO BLOCKS
          {
              name: "Set Left Leg Angle",
              icon: "./assets/blockicons/Servoleftleg.svg",
              type: "robot_servo_left",
              count: 90,
          },
          {
              name: "Set Right Leg Angle",
              icon: "./assets/blockicons/Servorightleg.svg",
              type: "robot_servo_right",
              count: 90,
          },
          {
              name: "Set Servo Speed",
              icon: "./assets/blockicons/servospeed.svg",
              type: "robot_servo_speed",
              count: 60,
          },
          {
              name: "Walk",
              icon: "./assets/blockicons/Walk.svg",
              type: "robot_walk",
              count: 4,
          },
          // 💡 LED CONTROL BLOCKS
          {
              name: "LED ON",
              icon: "./assets/blockicons/LedOn.svg",
              type: "robot_led_on",
        },
        {
            name: "LED OFF",
            icon: "./assets/blockicons/LedOff.svg",
            type: "robot_led_off",
        },
        {
            name: "Set LED Color",
            icon: "./assets/blockicons/LedColor.svg",
            type: "robot_led_color",
            // ledColor: "#FF0000",
        },
          { name: "LED Ring ON", icon: "./assets/blockicons/LedRingOn.svg", type: "robot_led_ring_on" },
          { name: "LED Ring OFF", icon: "./assets/blockicons/LedRingOff.svg", type: "robot_led_ring_off"},
          { name: "Set LED Ring Color", icon: "./assets/blockicons/LedRingColor.svg", type: "robot_led_ring_color", ledRingColor: "#00FF00"},
          { name: "LED Ring Rainbow", icon: "./assets/blockicons/LedRainbow.svg", type: "robot_led_ring_rainbow"},
          { name: "Set Ring LED Color", icon: "./assets/blockicons/SetRingLed.svg", type: "robot_led_ring_index", ledRingColor: "#00FF00", count: 0 },
          { name: "Set All Ring LEDs", icon: "./assets/blockicons/SetAllLed.svg", type: "robot_led_ring_all", ledRingColor: "#00FF00" },
        {
            name: "Set LED Brightness",
            icon: "./assets/blockicons/LEDbright.svg",
            type: "robot_led_brightness",
            count: 100, // 0-100%
        },
        // 🔊 SOUND BLOCKS
        //   {
        //       name: "Play Tone",
        //       icon: "./assets/blockicons/Speaker.svg",
        //       type: "robot_play_tone",
        //       frequency: 440,
        //       duration: 500,
        //   },
          {
              name: "Robot Sound",
              icon: "./assets/blockicons/robotsound.svg",
              type: "robot_sound_effect",
              soundType: "happy"
          },
        //   {
        //       name: "Play Buzzer",
        //       icon: "./assets/blockicons/Speaker.svg",
        //       type: "robot_buzzer",
        //   },
        // 📡 SENSOR BLOCKS
        // {
        //     name: "Distance Sensor",
        //     icon: "./assets/blockicons/Sensor.svg",
        //     type: "robot_distance",
        // },
    ],

    stembot: [
        {
            name: "Object Detection",
            icon: "./assets/ui/Objectdetction.svg",
            type: "cardMode",
        },
        {
            name: "Scan QR/Barcode",
            icon: "./assets/ui/QR.svg",
            type: "qrBarcodeScan",
        },
        {
            name: "Show QR Data",
            icon: "./assets/ui/ShowQR.svg",
            type: "qrShowData",
        },
        {
            name: "Run QR Commands",
            icon: "./assets/ui/RunQR.svg",
            type: "qrRunCommands",
        },
        {
            name: "Detect Number (Printed)",
            icon: "./assets/ui/Numberdetection.svg",
            type: "numberDetect",
        },
        {
            name: "Set AI Persona",
            icon: "./assets/ui/SetAI.svg",
            type: "aiSetPersona",
            persona: "ai",
        },
        {
            name: "Set Word Limit",
            icon: "./assets/ui/WordLimit.svg",
            type: "aiWordLimit",
            count: 40,
        },
        {
            name: "Set Creativity",
            icon: "./assets/ui/setcreativity.svg",
            type: "aiCreativity",
            count: 0.7,
        },
        {
            name: "Set Prompt",
            icon: "./assets/ui/SetPrompt.svg",
            type: "aiPrompt",
            message: "hi",
        },
        {
            name: "Get Response",
            icon: "./assets/ui/GetResponse.svg",
            type: "aiGetResponse",
        },
        {
            name: "Highlight POS",
            icon: "./assets/ui/Highlight.svg",
            type: "aiHighlightPos",
            posType: "noun",
        },
        {
            name: "AI Response",
            icon: "./assets/blockicons/LetterGet_Blue.svg",
            type: "aiResponse",
        },
        {
            name: "Speech To Text",
            icon: "./assets/ui/Speechtotext.svg",
            type: "speechToText",
        },
    ],

};


// Camera Control Block Component - KEEP AS IS
function CameraControlBlock({ puzzleBg }) {
    const dispatch = useDispatch();
    const globalCameraState = useSelector((s) => s.scene.globalCameraState);
    const [showDropdown, setShowDropdown] = useState(false);

    const handleCameraToggle = (newState) => {
        dispatch(setCameraState(newState));
        if (newState === "on") {
            window.humanDetectionController?.startCamera();
        } else {
            window.humanDetectionController?.stopCamera();
        }
        setShowDropdown(false);
    };

    const handleArrowClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowDropdown((prev) => !prev);
    };

    const handleDragStart = (event) => {
        setShowDropdown(false);
        const blockData = {
            name: "Camera Control",
            type: "camera_control",
            category: "humandetection",
            puzzleBg: puzzleBg,
            cameraState: globalCameraState,
        };
        event.dataTransfer.setData("application/block", JSON.stringify(blockData));
        event.dataTransfer.effectAllowed = "copy";
    };

    const handleTouchStart = (event) => {
        const blockData = {
            name: "Camera Control",
            type: "camera_control",
            category: "humandetection",
            puzzleBg: puzzleBg,
            cameraState: globalCameraState,
        };
        startTouchDrag(event, {
            dataTransferData: {
                "application/block": JSON.stringify(blockData),
                "text/plain": "Camera Control",
            },
            previewElement: event.currentTarget,
            dropEffect: "copy",
        });
    };

    return (
        <div
            className="camera-control-block block-palette-tile"
            draggable
            onDragStart={handleDragStart}
            onTouchStart={handleTouchStart}
        >
            <img className="block-bg" src={puzzleBg} alt="" draggable={false} />
            <img
                className="camera-icon"
                src="./assets/blockicons/camera.svg"
                alt="Camera"
                draggable={false}
            />
            <div className="camera-dropdown-arrow" onClick={handleArrowClick}>
                <img
                    src="./assets/blockicons/dropdown.svg"
                    alt="Options"
                    style={{ width: '16px', height: '16px' }}
                />
            </div>
            {showDropdown && (
                <div className="camera-dropdown-list show">
                    <div
                        className={`camera-dropdown-item ${globalCameraState === 'on' ? 'active' : ''}`}
                        onClick={() => handleCameraToggle('on')}
                    >
                        ON
                    </div>
                    <div
                        className={`camera-dropdown-item ${globalCameraState === 'off' ? 'active' : ''}`}
                        onClick={() => handleCameraToggle('off')}
                    >
                        OFF
                    </div>
                </div>
            )}
        </div>
    );
}

// 🛤️ ADDED: Path Color Block Component with color picker
function PathColorBlock({ block, puzzleBg, onDragStart, onTouchStart, onDoubleClick }) {
    const [selectedColor, setSelectedColor] = useState(block.pathColor || "#000000");
    const [showPicker, setShowPicker] = useState(false);
    const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
    const tileRef = useRef(null);

    useEffect(() => {
        if (block.pathColor && block.pathColor !== selectedColor) {
            setSelectedColor(block.pathColor);
        }
    }, [block.pathColor, selectedColor]);

    const updateColor = (newColor) => {
        const hex = newColor.toUpperCase();
        if (!/^#([0-9A-F]{3}){1,2}$/i.test(hex)) return;
        setSelectedColor(hex);
        block.pathColor = hex;
    };

    const openPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = tileRef.current?.getBoundingClientRect();
        if (rect) {
            setPickerPos({
                top: rect.bottom + window.scrollY + 6,
                left: rect.left + window.scrollX + rect.width / 2 - 90,
            });
        }
        setShowPicker(true);
    };

    const closePicker = () => setShowPicker(false);

    return (
        <>
            <div
                ref={tileRef}
                className="path-color-block block-palette-tile"
                draggable
                onDragStart={(e) =>
                    onDragStart({
                        ...block,
                        type: "setPathColor",
                        pathColor: selectedColor
                    })(e)
                }
                onTouchStart={(e) =>
                    onTouchStart({
                        ...block,
                        type: "setPathColor",
                        pathColor: selectedColor
                    })(e)
                }
                onDoubleClick={() =>
                    onDoubleClick({
                        ...block,
                        type: "setPathColor",
                        pathColor: selectedColor
                    })
                }
                onClick={openPicker}
                style={{ cursor: "pointer" }}
            >
                <img className="block-bg" src={puzzleBg} alt="" draggable={false} />
                <img className="block-icon" src={block.icon} alt={block.name} draggable={false} />
            </div>

            <ColorPickerPopover
                open={showPicker}
                position={pickerPos}
                value={selectedColor}
                onChange={updateColor}
                onClose={closePicker}
            />
        </>
    );
}

// 🤖 Robot LED Color Block Component (matching your PathColorBlock pattern)
function RobotLEDBlock({ block, puzzleBg, onDragStart, onTouchStart, onDoubleClick }) {
    const [selectedColor, setSelectedColor] = useState(block.ledColor || "#FF0000");
    const [showPicker, setShowPicker] = useState(false);
    const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
    const tileRef = useRef(null);

    useEffect(() => {
        if (block.ledColor && block.ledColor !== selectedColor) {
            setSelectedColor(block.ledColor);
        }
    }, [block.ledColor, selectedColor]);

    const updateColor = (newColor) => {
        const hex = newColor.toUpperCase();
        if (!/^#([0-9A-F]{3}){1,2}$/i.test(hex)) return;
        setSelectedColor(hex);
        block.ledColor = hex;

        // Convert hex to RGB and update robot
        if (window.robot) {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            window.robot.setLED(r, g, b);
        }
    };

    const openPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = tileRef.current?.getBoundingClientRect();
        if (rect) {
            setPickerPos({
                top: rect.bottom + window.scrollY + 6,
                left: rect.left + window.scrollX + rect.width / 2 - 90,
            });
        }
        setShowPicker(true);
    };

    const closePicker = () => setShowPicker(false);

    return (
        <>
            <div
                ref={tileRef}
                className="robot-led-block block-palette-tile"
                draggable
                onDragStart={(e) =>
                    onDragStart({
                        ...block,
                        type: "robot_led_color",
                        ledColor: selectedColor
                    })(e)
                }
                onTouchStart={(e) =>
                    onTouchStart({
                        ...block,
                        type: "robot_led",
                        ledColor: selectedColor
                    })(e)
                }
                onDoubleClick={() =>
                    onDoubleClick({
                        ...block,
                        type: "robot_led",
                        ledColor: selectedColor
                    })
                }
                onClick={openPicker}
                style={{ cursor: "pointer" }}
            >
                <img className="block-bg" src={puzzleBg} alt="" draggable={false} />
                <img className="block-icon" src={block.icon} alt={block.name} draggable={false} />

                {/* LED Color Indicator */}
                {/* <div
                    style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: selectedColor,
                        border: '2px solid white',
                        boxShadow: `0 0 8px ${selectedColor}`
                    }}
                /> */}
            </div>

            <ColorPickerPopover
                open={showPicker}
                position={pickerPos}
                value={selectedColor}
                onChange={updateColor}
                onClose={closePicker}
            />
        </>
    );
}


// 🎨 NEW: Color Detection Block Component with color picker
function ColorDetectionBlock({ block, puzzleBg, onDragStart, onTouchStart, onDoubleClick }) {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedColor, setSelectedColor] = useState(block.targetColor || "#FF0000");

    const handleColorChange = (color) => {
        setSelectedColor(color);
        block.targetColor = color;
        setShowColorPicker(false);
    };

    const handleColorClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowColorPicker(!showColorPicker);
    };

    const colors = ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#FFA500", "#800080"];

    return (
        <div
            className="color-detection-block block-palette-tile"
            draggable
            onDragStart={(e) => onDragStart({ ...block, targetColor: selectedColor })(e)}
            onTouchStart={(e) => onTouchStart({ ...block, targetColor: selectedColor })(e)}
            onDoubleClick={() => onDoubleClick({ ...block, targetColor: selectedColor })}
        >
            <img className="block-bg" src={puzzleBg} alt="" draggable={false} />
            <img
                className="block-icon"
                src={block.icon}
                alt={block.name}
                draggable={false}
            />
            {/* <div
                className="color-indicator"
                onClick={handleColorClick}
                style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: selectedColor,
                    border: '2px solid white',
                    cursor: 'pointer',
                    zIndex: 10
                }}
            /> */}
            {showColorPicker && (
                <div
                    className="color-picker-dropdown"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        zIndex: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        width: '120px'
                    }}
                >
                    {colors.map(color => (
                        <div
                            key={color}
                            onClick={() => handleColorChange(color)}
                            style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: color,
                                border: selectedColor === color ? '3px solid #333' : '1px solid #ccc',
                                cursor: 'pointer'
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// Voice Recording Modal Component - KEEP AS IS
function VoiceRecordModal({ isOpen, onClose, onSave }) {
    const [isRecording, setIsRecording] = useState(false);
    const [audioURL, setAudioURL] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioRef = useRef(null);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);
                setAudioURL(url);
                setAudioBlob(blob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Could not access microphone. Please check permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const playRecording = () => {
        if (audioURL && !isPlaying) {
            audioRef.current = new Audio(audioURL);
            audioRef.current.onended = () => setIsPlaying(false);
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const stopPlayback = () => {
        if (audioRef.current && isPlaying) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    };

    const saveRecording = () => {
        if (audioURL && audioBlob) {
            const soundId = `custom_sound_${Date.now()}`;
            const customSoundData = {
                id: soundId,
                name: `My Sound ${new Date().toLocaleTimeString()}`,
                audioURL: audioURL,
                audioBlob: audioBlob,
                type: 'custom'
            };
            onSave(customSoundData);
            onClose();
        }
    };

    const handleClose = () => {
        if (isRecording) {
            stopRecording();
        }
        if (isPlaying) {
            stopPlayback();
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        setAudioURL(null);
        setAudioBlob(null);
        setRecordingTime(0);
        setIsPlaying(false);
        onClose();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    if (!isOpen) return null;

    return (
        <div className="voice-modal-overlay">
            <div className="voice-modal">
                <div className="voice-modal-header">
                    <img src="./assets/lib/mic.svg" alt="Microphone" className="modal-mic-icon" />
                    <button className="modal-close-btn" onClick={handleClose}>×</button>
                </div>

                <div className="voice-modal-content">
                    <div className="waveform-display">
                        {Array.from({ length: 15 }).map((_, i) => (
                            <div
                                key={i}
                                className={`waveform-bar ${isRecording ? 'recording' : ''}`}
                                style={{ animationDelay: `${i * 0.1}s` }}
                            />
                        ))}
                    </div>

                    <div className="recording-time">{formatTime(recordingTime)}</div>

                    <div className="voice-controls">
                        <button
                            className="control-btn record-control"
                            onClick={isRecording ? stopRecording : startRecording}
                            title={isRecording ? "Stop Recording" : "Start Recording"}
                        >
                            <img
                                src={isRecording ? "./assets/lib/recordon.svg" : "./assets/lib/recordoff.svg"}
                                alt={isRecording ? "Recording" : "Record"}
                            />
                        </button>

                        {audioURL && (
                            <button
                                className="control-btn play-control"
                                onClick={isPlaying ? stopPlayback : playRecording}
                                title={isPlaying ? "Stop Playback" : "Play Recording"}
                            >
                                <img
                                    src={isPlaying ? "./assets/lib/playon.svg" : "./assets/lib/playoff.svg"}
                                    alt={isPlaying ? "Playing" : "Play"}
                                />
                            </button>
                        )}

                        {(isRecording || isPlaying) && (
                            <button
                                className="control-btn stop-control"
                                onClick={isRecording ? stopRecording : stopPlayback}
                                title="Stop"
                            >
                                <img
                                    src={isRecording || isPlaying ? "./assets/lib/stopon.svg" : "./assets/lib/stopoff.svg"}
                                    alt="Stop"
                                />
                            </button>
                        )}
                    </div>

                    {audioURL && (
                        <button className="save-btn" onClick={saveRecording}>
                            ✓ Save Recording
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function BlockPalette({ isPaletteOpen }) {
    const dispatch = useDispatch();
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [showConnectionModal, setShowConnectionModal] = useState(false); // Added connection modal state
    // --- ADDED: Pin modal state & selected pin block ---
    const [showPinModal, setShowPinModal] = useState(false);
    const [selectedPinBlock, setSelectedPinBlock] = useState(null);
    const handlePinBlockClick = (block) => {
        if (["setDigitalPin", "setPWM", "setDAC", "readAnalogPin", "readDigitalPin"].includes(block.type)) {
            setSelectedPinBlock(block);
            setShowPinModal(true);
        }
    };
    const selectedBlockCategory = useSelector((s) => s.scene.selectedBlockCategory) || "motion";
    const customSounds = useSelector((s) => s.scene.customSounds) || [];

    // Get blocks and add custom sound blocks if in sound category
    const getBlocks = () => {
        const baseBlocks = blocksByCategory[selectedBlockCategory] || [];

        if (selectedBlockCategory === 'sound') {
            const customSoundBlocks = customSounds.map(sound => ({
                name: sound.name,
                icon: "./assets/lib/mic.svg",
                type: 'custom_sound',
                soundData: sound
            }));
            return [...baseBlocks, ...customSoundBlocks];
        }

        return baseBlocks;
    };

    const blocks = getBlocks();
    const puzzleBg = puzzleBgByCategory[selectedBlockCategory] || "./assets/blocks/blueCmd.svg";
    const barColor = barColorByCategory[selectedBlockCategory] || "#3291d7";

    // Optional workspaceBlocks used by blockly/workspace UI when category == esp32
    //If you have more ESP32-related blocks (like PWM, DAC, Servo, etc.), you can simply append them in the blocks array under this ESP32 section.
    let workspaceBlocks = [];
    if (selectedBlockCategory === "esp32") {
        workspaceBlocks = [
            {
                name: "Pins",
                blocks: [
                    {
                        type: "set_pin_mode",
                        message0: "set pin %1 mode %2",
                        args0: [
                            { type: "field_dropdown", name: "PIN", options: [["IO2", "IO2"], ["IO4", "IO4"], ["IO5", "IO5"], ["IO12", "IO12"], ["IO13", "IO13"], ["IO14", "IO14"], ["IO15", "IO15"], ["IO25", "IO25"], ["IO26", "IO26"], ["IO27", "IO27"]] },
                            { type: "field_dropdown", name: "MODE", options: [["input", "INPUT"], ["output", "OUTPUT"]] }
                        ],
                        colour: "#3399ff",
                        tooltip: "Set pin mode (input/output)"
                    },
                    {
                        type: "set_digital_pin",
                        message0: "set digital pin %1 out %2",
                        args0: [
                            { type: "field_dropdown", name: "PIN", options: [["IO2", "IO2"], ["IO4", "IO4"], ["IO5", "IO5"], ["IO12", "IO12"], ["IO13", "IO13"], ["IO14", "IO14"], ["IO15", "IO15"]] },
                            { type: "field_dropdown", name: "STATE", options: [["high", "HIGH"], ["low", "LOW"]] }
                        ],
                        colour: "#33cc33",
                        tooltip: "Set a digital pin to HIGH or LOW"
                    },
                    {
                        type: "read_digital_pin",
                        message0: "read digital pin %1",
                        args0: [
                            { type: "field_dropdown", name: "PIN", options: [["IO2", "IO2"], ["IO4", "IO4"], ["IO5", "IO5"], ["IO12", "IO12"]] }
                        ],
                        output: "Number",
                        colour: "#ff9933",
                        tooltip: "Read digital pin state"
                    },
                    {
                        type: "read_analog_pin",
                        message0: "read analog pin %1",
                        args0: [
                            { type: "field_dropdown", name: "PIN", options: [["IO32", "IO32"], ["IO33", "IO33"], ["IO34", "IO34"], ["IO35", "IO35"]] }
                        ],
                        output: "Number",
                        colour: "#ffcc00",
                        tooltip: "Read analog value from pin"
                    }
                ]
            }
        ];
    }

    const handleDoubleClick = (block) => {
        // FIXED: Add proper category to blocks
        const blockWithCategory = {
            ...block,
            category: selectedBlockCategory,
            puzzleBg: puzzleBg
        };

        // Add execution data for custom sounds
        if (block.type === 'custom_sound') {
            const executableBlock = {
                ...blockWithCategory,
                execute: () => {
                    const audio = new Audio(block.soundData.audioURL);
                    audio.play().catch(err => console.error('Error playing sound:', err));
                }
            };
            dispatch(addBlockToScript({ actorId: null, block: executableBlock }));
        } else {
            dispatch(addBlockToScript({ actorId: null, block: blockWithCategory }));
        }
    };

    const buildBlockPayload = (block) => ({
        ...block,
        category: selectedBlockCategory,
        puzzleBg: block.puzzleBg || puzzleBgByCategory[selectedBlockCategory],
    });

    const handleDragStart = (block) => (event) => {
        const data = buildBlockPayload(block);
        event.dataTransfer.setData("application/block", JSON.stringify(data));
        event.dataTransfer.effectAllowed = "copy";
    };

    const handleTouchDragStart = (block) => (event) => {
        if (!event) return;
        const data = buildBlockPayload(block);
        startTouchDrag(event, {
            dataTransferData: {
                "application/block": JSON.stringify(data),
                "text/plain": block?.name || "block",
            },
            previewElement: event.currentTarget,
            dropEffect: "copy",
        });
    };

    const handleBlockClick = (block) => {
        console.log('Block clicked:', block.name); // DEBUG
        // Open pin configuration modal for pin-related blocks
        if (["setDigitalPin", "setPWM", "setDAC", "readAnalogPin", "readDigitalPin"].includes(block.type)) {
            handlePinBlockClick(block);
            return;
        }
        if (block.name === "Record") {
            console.log('Opening voice modal'); // DEBUG
            setShowVoiceModal(true);
        } else if (block.requiresConnection) { // Added device connection handling
            setShowConnectionModal(true);
        }
    };

    const handleVoiceSave = (customSoundData) => {
        dispatch(addCustomSound(customSoundData));
        console.log('Custom sound saved:', customSoundData);
    };

    // Added: device connect handler used by ConnectionModal
    const handleDeviceConnect = (deviceName, connection) => {
        console.log("Connected to:", connection);
        alert(`Connected via ${connection.type}`);
    };


    return (
        <>
            {isPaletteOpen && (
                <div
                    className="block-palette-root"
                    style={{
                        background: barColor,
                        transition: "background 0.18s",
                    }}
                >
                    {blocks.map((block, idx) => {
                        // Special handling for camera control blocks
                        if (block.type === "camera_control") {
                            return <CameraControlBlock key={idx} puzzleBg={puzzleBg} />;
                        }

                        // 🛤️ ADDED: Special handling for path color blocks
                        if (block.type === "setPathColor") {
                            return (
                                <PathColorBlock
                                    key={idx}
                                    block={block}
                                    puzzleBg={puzzleBg}
                                    onDragStart={handleDragStart}
                                    onTouchStart={handleTouchDragStart}
                                    onDoubleClick={handleDoubleClick}
                                />
                            );
                        }

                        // 🤖 Special handling for robot LED blocks
                        if (block.type === "robot_led_color" || block.type === "robot_led") {
                            return (
                                <RobotLEDBlock
                                    key={idx}
                                    block={block}
                                    puzzleBg={puzzleBg}
                                    onDragStart={handleDragStart}
                                    onTouchStart={handleTouchDragStart}
                                    onDoubleClick={handleDoubleClick}
                                />
                            );
                        }

                        // 🎨 FIXED: Special handling for color detection blocks
                        if (block.type === "startColorDetection" || block.type === "colorDetected") {
                            console.log('🎨 Rendering ColorDetectionBlock for:', block.name); // DEBUG
                            return (
                                <ColorDetectionBlock
                                    key={idx}
                                    block={block}
                                    puzzleBg={puzzleBg}
                                    onDragStart={handleDragStart}
                                    onTouchStart={handleTouchDragStart}
                                    onDoubleClick={handleDoubleClick}
                                />
                            );
                        }

                        if (!isPaletteOpen) return null; // <--- Add this guard

                        const tilePuzzleBg = block.puzzleBg || puzzleBg;
                        const isRepeatTile = block.type === "repeat";

                        return (
                            <div
                                className={`block-palette-tile ${isRepeatTile ? 'repeat-tile' : ''} ${block.type === 'custom_sound' ? 'custom-sound-block' : ''}`}
                                key={(block.name || "end") + idx}
                                title={block.name || "End"}
                                draggable={!block.requiresConnection} // Modified: Only draggable if no connection required
                                onDragStart={!block.requiresConnection ? handleDragStart(block) : undefined} // Modified: Only add drag handler if draggable
                                onTouchStart={!block.requiresConnection ? handleTouchDragStart(block) : undefined}
                                onDoubleClick={() => handleDoubleClick(block)}
                                onClick={() => handleBlockClick(block)}
                                style={{ cursor: block.requiresConnection ? 'pointer' : 'grab' }} // Modified: Different cursor for connection blocks
                            >
                                <img className="block-bg" src={tilePuzzleBg} alt="" draggable={false} aria-hidden="true" />

                                {block.icon && (
                                    <img
                                        className="block-icon"
                                        src={block.icon}
                                        alt={block.name}
                                        draggable={false}
                                    />
                                )}

                                {typeof block.label === "string" && block.label.trim() !== "" && (
                                    <span className="block-label">{block.label}</span>
                                )}

                                {block.type === 'custom_sound' && (
                                    <div className="custom-sound-indicator">🎤</div>
                                )}

                                {/* 🛤️ EXISTING: Special indicators for path following blocks */}
                                {block.type === 'followPath' && (
                                    <div className="path-follow-indicator" style={{
                                        position: 'absolute', bottom: '4px', right: '4px',
                                        fontSize: '12px', color: 'white'
                                    }}></div>
                                )}

                                {block.type === 'pathDetected' && (
                                    <div className="path-detect-indicator" style={{
                                        position: 'absolute', bottom: '4px', right: '4px',
                                        fontSize: '12px', color: 'white'
                                    }}></div>
                                )}

                                {/* 🎨 NEW: Special indicators for color detection blocks */}
                                {block.type === 'startColorDetection' && (
                                    <div className="color-detect-start-indicator" style={{
                                        position: 'absolute', bottom: '4px', right: '4px',
                                        fontSize: '12px', color: 'white'
                                    }}></div>
                                )}

                                {block.type === 'colorDetected' && (
                                    <div className="color-detected-indicator" style={{
                                        position: 'absolute', bottom: '4px', right: '4px',
                                        fontSize: '12px', color: 'white'
                                    }}></div>
                                )}

                                {block.type === 'followPathOnColor' && (
                                    <div className="follow-on-color-indicator" style={{
                                        position: 'absolute', bottom: '4px', right: '4px',
                                        fontSize: '12px', color: 'white'
                                    }}></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            <VoiceRecordModal
                isOpen={showVoiceModal}
                onClose={() => setShowVoiceModal(false)}
                onSave={handleVoiceSave}
            />

            {/* --- PIN CONFIGURATION MODAL --- */}
            <PinConfigModal
                isOpen={showPinModal}
                onClose={() => setShowPinModal(false)}
                initialData={selectedPinBlock || {}}
                onSave={(data) => {
                    console.log("💾 Pin Config Saved:", data);
                    if (selectedPinBlock) {
                        selectedPinBlock.pin = data.pin;
                        selectedPinBlock.mode = data.mode;
                        selectedPinBlock.state = data.state;
                    }
                    setShowPinModal(false);
                }}
            />

            {/* Added ConnectionModal */}
            <ConnectionModal
                isOpen={showConnectionModal}
                onClose={() => setShowConnectionModal(false)}
                onConnect={handleDeviceConnect}
            />
        </>
    );
}

export { blocksByCategory };